import React, { useEffect, useState, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { getBasicSetting } from 'src/constrains/GlobalProvider/slice';
import { generateKey } from 'src/tools/getUserInfo';
import { EPlanType, EPlanStatus } from 'src/types/common/index';
import { InfoCircleFilled } from '@ant-design/icons';
import { history } from 'src/router/history';

import css from './css.module.less';
import {
  getI18nFromGeneral,
  replaceI18nVariables,
  handleOnFormatStoreDate,
} from '@/src/tools/tools';

const PLAN_REMINDER = 'PLAN_REMINDER';
const UPGRADE_ROUTE = '/settings/billing';

interface IContentConfigItem {
  descKey: string;
  linkKey: string;
}

const CONTENT_CONFIG: Record<string, IContentConfigItem> = {
  [`${EPlanType.TRIAL}_${EPlanStatus.ACTIVE}`]: {
    descKey: 'general.planAndStatus.freeActiveDesc',
    linkKey: 'general.planAndStatus.freeActiveLink',
  },
  [`${EPlanType.TRIAL}_${EPlanStatus.EXPIRED}`]: {
    descKey: 'general.planAndStatus.freeExpiredDesc',
    linkKey: 'general.planAndStatus.freeExpiredLink',
  },
  [`${EPlanType.BASIC}_${EPlanStatus.PENDING}`]: {
    descKey: 'general.planAndStatus.basicPendingDesc',
    linkKey: 'general.planAndStatus.basicPendingLink',
  },
  [`${EPlanType.BASIC}_${EPlanStatus.EXPIRED}`]: {
    descKey: 'general.planAndStatus.basicExpiredDesc',
    linkKey: 'general.planAndStatus.purchaseAPlanLink',
  },
  [`${EPlanType.BASIC}_${EPlanStatus.CANCELLED}`]: {
    descKey: 'general.planAndStatus.basicCancelledDesc',
    linkKey: 'general.planAndStatus.purchaseAPlanLink',
  },
  [`${EPlanType.ENTERPRISE}_${EPlanStatus.EXPIRED}`]: {
    descKey: 'general.planAndStatus.enterpriseExpiredDesc',
    linkKey: 'general.planAndStatus.purchaseAPlanLink',
  },
};

const createContent = (text: string, linkText: string): ReactNode => {
  const PLACEHOLDER = '{link}';
  if (text.includes(PLACEHOLDER)) {
    const parts = text.split(PLACEHOLDER);
    return (
      <>
        {parts[0]}
        <span className={css.link} onClick={() => history.push(UPGRADE_ROUTE)}>
          {linkText}
        </span>
        {parts[1]}
      </>
    );
  }
};

const GlobalPlanStatusModal: React.FC = () => {
  const basicSetting = useSelector(getBasicSetting());
  const [visible, setVisible] = useState(false);
  const [content, setContent] = useState<ReactNode>('');

  const handleClose = () => {
    const storageKey = generateKey(PLAN_REMINDER);
    localStorage.setItem(storageKey, 'true');
    setVisible(false);
  };

  useEffect(() => {
    if (!basicSetting || Object.keys(basicSetting).length === 0) {
      setVisible(false);
      setContent('');
      return;
    }

    const storageKey = generateKey(PLAN_REMINDER);
    const planType = get(basicSetting, 'planType');
    const planStatus = get(basicSetting, 'planStatus');
    const expireDate = get(basicSetting, 'expireDate', '');
    const formattedExpireDate = expireDate
      ? handleOnFormatStoreDate(expireDate)
      : '';

    if (!planType || !planStatus) {
      setVisible(false);
      setContent('');
      return;
    }

    if (!localStorage.getItem(storageKey)) {
      localStorage.setItem(storageKey, 'false');
    }

    if (localStorage.getItem(storageKey) === 'true') {
      setVisible(false);
      return;
    }

    const configKey = `${planType}_${planStatus}`;

    const config = CONTENT_CONFIG[configKey];

    if (!config) {
      setVisible(false);
      setContent('');
      return;
    }
    let desc = getI18nFromGeneral(config.descKey);
    const linkText = getI18nFromGeneral(config.linkKey);

    desc = replaceI18nVariables(desc, formattedExpireDate || '');

    const nextContent = createContent(desc, linkText);
    setContent(nextContent);

    setVisible(true);
  }, [basicSetting]);

  if (!visible || !content) {
    return null;
  }

  return (
    <div className={css.wrapper}>
      <div className={css.card}>
        <div className={css.header}>
          <div className={css.title}>
            <InfoCircleFilled
              rev=''
              className={css.headerIcon}
              style={{ color: '#ff695f' }}
            />
            {getI18nFromGeneral('general.planAndStatus.reminder')}
          </div>
          <span className={css.closeIcon} onClick={handleClose}>
            ×
          </span>
        </div>
        <div className={css.content}>{content}</div>
      </div>
    </div>
  );
};

export default GlobalPlanStatusModal;
